import { CircleAlert, CircleCheck, Cpu, HandCoins, Play, RefreshCw, Send, Server } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatDateTimeValue, formatNumber } from '@/lib/utils';

import { useEstadoServicios, useMutarServicios, useServiciosPts } from '../servicios.api';
import type { ColaComprobantes, ColaOutbox, EstadoBoleteo } from '../servicios.types';

/** Mensaje legible de una mutación fallida. */
function mensajeError(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;

  return porDefecto;
}

/** Fila de dato con su etiqueta. */
function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-ink-100 py-1.5 last:border-0">
      <dt className="text-xs text-ink-500">{etiqueta}</dt>
      <dd className="text-right text-sm font-medium text-ink-800">{children}</dd>
    </div>
  );
}

/** Muestra un valor del controlador o avisa que no lo reporta. */
function ValorDelControlador({ valor, sufijo }: { valor: string | number | null; sufijo?: string }) {
  if (valor === null || valor === '') {
    return <span className="text-xs font-normal text-ink-400">no reportado</span>;
  }

  return (
    <>
      {valor}
      {sufijo ? ` ${sufijo}` : ''}
    </>
  );
}

/** Tarjeta del controlador de surtidores. */
function TarjetaPts() {
  const consulta = useServiciosPts();

  if (consulta.isLoading) {
    return (
      <SectionCard title="Conexión con el controlador PTS">
        <Skeleton className="h-40 w-full" />
      </SectionCard>
    );
  }

  if (consulta.isError || !consulta.data) {
    return (
      <SectionCard title="Conexión con el controlador PTS">
        <Alert variant="error" title="No se pudo leer la configuración del PTS">
          {mensajeError(consulta.error, 'Vuelve a intentarlo en unos segundos.')}
        </Alert>
      </SectionCard>
    );
  }

  const datos = consulta.data;
  const { configuracion, conexion, controlador, equipo, noSoportados } = datos;

  return (
    <SectionCard
      title="Conexión con el controlador PTS"
      description="Cómo está configurada la comunicación con el controlador de surtidores y si en este momento responde."
      action={
        <Button
          variant="outline"
          size="sm"
          className="whitespace-nowrap"
          leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          loading={consulta.isFetching}
          onClick={() => void consulta.refetch()}
        >
          Comprobar
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {conexion.conectado ? (
            <Badge variant="success">
              <CircleCheck className="mr-1 size-3.5" aria-hidden="true" />
              El equipo responde
            </Badge>
          ) : (
            <Badge variant="danger">
              <CircleAlert className="mr-1 size-3.5" aria-hidden="true" />
              Sin conexión
            </Badge>
          )}
          <span className="text-xs text-ink-500">
            Revisado el {formatDateTimeValue(conexion.revisadoEn)}
          </span>
        </div>

        {conexion.conectado ? null : (
          <Alert variant="warning" title="El controlador no responde">
            {conexion.motivo ?? 'No se pudo contactar con el controlador.'} Revisa que el equipo esté
            encendido y que la dirección configurada sea la correcta.
          </Alert>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">Nuestra configuración</h3>
            <dl>
              <Dato etiqueta="Dirección del controlador">
                <span className="break-all font-mono text-xs">{configuracion.url}</span>
              </Dato>
              <Dato etiqueta="Usuario">{configuracion.usuario}</Dato>
              <Dato etiqueta="Tiempo de espera">{configuracion.timeoutMs} ms</Dato>
              <Dato etiqueta="Telemetría de tanques">
                {configuracion.telemetriaTanques ? 'Activada' : 'Desactivada'}
              </Dato>
              <Dato etiqueta="Sistema">{configuracion.sistema ?? '—'}</Dato>
              <Dato etiqueta="Tipo de turno">{configuracion.tipoTurno ?? '—'}</Dato>
            </dl>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">Datos del equipo</h3>
            <dl>
              <Dato etiqueta="Identificador de configuración">
                <ValorDelControlador valor={controlador?.identificadorConfiguracion ?? null} />
              </Dato>
              <Dato etiqueta="Tipo de controlador">
                <ValorDelControlador valor={controlador?.tipo ?? null} />
              </Dato>
              <Dato etiqueta="Firmware">
                <ValorDelControlador valor={controlador?.firmware ?? null} />
              </Dato>
              <Dato etiqueta="Fecha del equipo">
                <ValorDelControlador valor={controlador?.fechaHora ?? null} />
              </Dato>
              <Dato etiqueta="Batería">
                <ValorDelControlador
                  valor={controlador?.bateriaVoltios ?? null}
                  sufijo={controlador?.bateriaVoltios ? 'V' : undefined}
                />
              </Dato>
              <Dato etiqueta="Tarjeta SD">
                <ValorDelControlador valor={controlador?.tarjetaSd ?? null} />
              </Dato>
            </dl>
          </div>
        </div>

        {equipo ? (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">Equipo configurado</h3>
            <div className="flex flex-wrap gap-2">
              <Badge variant="brand">{equipo.conteos.puertos} puertos</Badge>
              <Badge variant="neutral">{equipo.conteos.bombas} bombas</Badge>
              <Badge variant="neutral">{equipo.conteos.mangueras} mangueras</Badge>
              <Badge variant="neutral">{equipo.conteos.grados} grados</Badge>
            </div>

            {equipo.grados.length > 0 ? (
              <div className="mt-3 overflow-hidden rounded-control border border-ink-100">
                <table className="w-full text-sm">
                  <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Grado</th>
                      <th className="px-3 py-2 text-left font-semibold">Producto</th>
                      <th className="px-3 py-2 text-right font-semibold">Precio del equipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipo.grados.map((grado) => (
                      <tr key={String(grado.id)} className="border-t border-ink-100">
                        <td className="px-3 py-2 text-ink-700">{grado.id ?? '—'}</td>
                        <td className="px-3 py-2 text-ink-800">{grado.nombre ?? '—'}</td>
                        <td className="px-3 py-2 text-right text-ink-700">
                          {grado.precio === null ? '—' : `S/ ${grado.precio.toFixed(2)}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        {noSoportados.length > 0 ? (
          <p className="text-xs text-ink-400">
            El equipo no reporta: {noSoportados.join(', ')}. Es normal en controladores que no
            implementan esos paquetes; el resto de datos sí se leen.
          </p>
        ) : null}
      </div>
    </SectionCard>
  );
}

/** Tarjeta del boleteo automático (nuestro ticketing). */
function TarjetaBoleteo({ boleteo, sistema }: { boleteo: EstadoBoleteo; sistema: string }) {
  const mutaciones = useMutarServicios();
  const [confirmar, setConfirmar] = useState(false);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);

  const ejecutar = () => {
    setAviso(null);

    mutaciones.ejecutarBoleteo.mutate(undefined, {
      onSuccess: (datos) => {
        setConfirmar(false);
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => {
        setAviso({
          tono: 'error',
          texto: mensajeError(error, 'No se pudo ejecutar el boleteo automático.'),
        });
      },
    });
  };

  return (
    <SectionCard
      title="Boleteo automático"
      description="Servicio propio del modo WEB que emite los comprobantes de las ventas libres que se pasaron del límite configurado."
      action={
        <Button
          size="sm"
          className="whitespace-nowrap"
          leftIcon={<Play className="size-4" aria-hidden="true" />}
          loading={mutaciones.ejecutarBoleteo.isPending}
          disabled={!boleteo.aplicable || !boleteo.disponible || !boleteo.habilitado}
          onClick={() => setConfirmar(true)}
        >
          Ejecutar ahora
        </Button>
      }
    >
      <div className="space-y-3">
        {aviso ? (
          <Alert variant={aviso.tono} title={aviso.tono === 'success' ? 'Boleteo ejecutado' : 'No se pudo ejecutar'}>
            {aviso.texto}
          </Alert>
        ) : null}

        {!boleteo.aplicable ? (
          <Alert variant="info" title={`No aplica en modo ${sistema}`}>
            {boleteo.detalle ??
              'El boleteo automático sólo opera cuando el sistema está configurado como WEB.'}
          </Alert>
        ) : boleteo.disponible ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={boleteo.habilitado ? 'success' : 'neutral'}>
                {boleteo.habilitado ? 'Habilitado' : 'Deshabilitado'}
              </Badge>
              <Badge variant={boleteo.activo ? 'success' : 'warning'}>
                {boleteo.activo ? 'Programado en ejecución' : 'Sin programación activa'}
              </Badge>
            </div>

            <dl>
              <Dato etiqueta="Frecuencia">
                {boleteo.intervaloMinutos === null ? '—' : `cada ${boleteo.intervaloMinutos} minutos`}
              </Dato>
              <Dato etiqueta="Programación">
                <span className="font-mono text-xs">{boleteo.cron ?? '—'}</span>
              </Dato>
            </dl>

            {boleteo.habilitado ? null : (
              <p className="text-xs text-ink-400">
                El servicio está deshabilitado en el servidor (AUTO_TICKETING_ENABLED): no emitirá
                comprobantes solo.
              </p>
            )}
          </>
        ) : (
          <Alert variant="warning" title="Servicio POS no disponible">
            {boleteo.detalle ?? 'No se pudo consultar el estado del boleteo.'}
          </Alert>
        )}
      </div>

      <ConfirmDialog
        open={confirmar}
        title="Ejecutar el boleteo ahora"
        description="Se revisan las ventas libres que pasaron el límite y se emiten sus comprobantes. Es la misma pasada que hace el servicio cada pocos minutos."
        confirmLabel="Ejecutar"
        tone="primary"
        loading={mutaciones.ejecutarBoleteo.isPending}
        onClose={() => setConfirmar(false)}
        onConfirm={ejecutar}
      />
    </SectionCard>
  );
}

/** Bloque de la cola de documentación propia. */
function BloqueOutbox({ outbox }: { outbox: ColaOutbox }) {
  const filas = [
    { etiqueta: 'Pendientes', valor: outbox.pendientes, tono: 'warning' as const },
    { etiqueta: 'Procesando', valor: outbox.procesando, tono: 'brand' as const },
    { etiqueta: 'Completados', valor: outbox.completados, tono: 'success' as const },
    { etiqueta: 'Fallidos', valor: outbox.fallidos, tono: 'danger' as const },
    { etiqueta: 'Omitidos', valor: outbox.omitidos, tono: 'neutral' as const },
  ];

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-ink-800">
        Nuestra cola de documentación
      </h3>
      <p className="mb-3 text-xs text-ink-500">
        Documentos de autoservicio que se emiten y se envían solos. Si un envío falla, el servicio
        lo reintenta.
      </p>

      <div className="flex flex-wrap gap-2">
        {filas.map((fila) => (
          <Badge key={fila.etiqueta} variant={fila.tono}>
            {fila.etiqueta}: {fila.valor}
          </Badge>
        ))}
      </div>

      <dl className="mt-3">
        <Dato etiqueta="Total en la cola">{outbox.total}</Dato>
        <Dato etiqueta="Reintentos acumulados">{outbox.reintentos}</Dato>
        <Dato etiqueta="Próximo intento">{formatDateTimeValue(outbox.proximoIntento, true)}</Dato>
        <Dato etiqueta="Último intento">{formatDateTimeValue(outbox.ultimoIntento, true)}</Dato>
      </dl>

      {outbox.total === 0 ? (
        <p className="mt-2 text-xs text-ink-400">
          La cola está vacía: no hay documentos esperando viaje.
        </p>
      ) : null}
    </div>
  );
}

/** Bloque de comprobantes en la cola de envío de SUNAT. */
function BloqueComprobantes({ comprobantes }: { comprobantes: ColaComprobantes }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-ink-800">Comprobantes en la cola de SUNAT</h3>
      <p className="mb-3 text-xs text-ink-500">
        Documentos electrónicos en estado <span className="font-mono">Queue</span> o{' '}
        <span className="font-mono">Processing</span>: están emitidos y esperando respuesta.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={comprobantes.enCola.total > 0 ? 'warning' : 'success'}>
          En cola: {comprobantes.enCola.total}
        </Badge>
        {comprobantes.enCola.porTipo.map((fila) => (
          <Badge key={fila.tipo} variant="outline">
            {fila.etiqueta}: {fila.total}
          </Badge>
        ))}
      </div>

      {comprobantes.porEstado.length > 0 ? (
        <div className="mt-3 overflow-hidden rounded-control border border-ink-100">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Estado</th>
                <th className="px-3 py-2 text-right font-semibold">Comprobantes</th>
              </tr>
            </thead>
            <tbody>
              {comprobantes.porEstado.map((fila) => (
                <tr key={fila.estado} className="border-t border-ink-100">
                  <td className="px-3 py-2 text-ink-700">{fila.estado}</td>
                  <td className="px-3 py-2 text-right text-ink-800">
                    {fila.total.toLocaleString('es-PE')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Estado de los comprobantes: pendientes y rechazados por origen, y total de
 * documentos electrónicos. Cada widget lleva a la vista de detalle.
 */
function TarjetaComprobantes({ comprobantes }: { comprobantes: ColaComprobantes }) {
  const { pendientes, rechazados, documentos } = comprobantes;
  const navegar = useNavigate();

  const ver = (origen: string, estado: 'pendientes' | 'rechazados' | 'todos') => {
    navegar(
      `/configuracion/servicios/documentos?origen=${encodeURIComponent(origen)}&estado=${estado}`,
    );
  };

  const widgets = [
    {
      origen: 'factura',
      etiqueta: 'Facturas pendientes',
      valor: pendientes.factura,
      rechazadas: rechazados.factura,
      criterio: 'factura.estado_api IS NULL',
    },
    {
      origen: 'boleta',
      etiqueta: 'Boletas pendientes',
      valor: pendientes.boleta,
      rechazadas: rechazados.boleta,
      criterio: 'boleta.estado_api IS NULL',
    },
    {
      origen: 'admin_comprobante',
      etiqueta: 'Administrativos pendientes',
      valor: pendientes.adminComprobantes,
      rechazadas: rechazados.adminComprobantes,
      criterio: 'admin_comprobante.estado_sunat = «En Validación»',
    },
    {
      origen: 'market',
      etiqueta: 'Market pendientes',
      valor: pendientes.market,
      rechazadas: rechazados.market,
      criterio: 'market sin respuesta de SUNAT',
    },
  ];

  return (
    <SectionCard
      title="Estados de los comprobantes"
      description="Pendientes y rechazados por origen, y total de documentos electrónicos registrados."
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {widgets.map((widget) => (
            <div
              key={widget.origen}
              className="flex flex-col rounded-card border border-ink-100 bg-white p-3"
            >
              <span className="flex items-start justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  {widget.etiqueta}
                </span>
                <Badge variant={widget.valor > 0 ? 'warning' : 'success'}>
                  {widget.valor > 0 ? 'Pendientes' : 'Al día'}
                </Badge>
              </span>
              <p className="mt-1 text-2xl font-bold tabular-nums text-ink-900">
                {formatNumber(widget.valor)}
              </p>
              <p className="mt-1 font-mono text-[10px] text-ink-400">{widget.criterio}</p>

              <p className="mt-1 text-[11px] text-ink-500">
                Rechazados:{' '}
                <span className={widget.rechazadas > 0 ? 'font-semibold text-rose-600' : ''}>
                  {formatNumber(widget.rechazadas)}
                </span>
              </p>

              <div className="mt-auto flex flex-col gap-1.5 pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => ver(widget.origen, 'pendientes')}
                >
                  Ver pendientes
                </Button>
                {widget.rechazadas > 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    className="text-rose-600 hover:text-rose-700"
                    onClick={() => ver(widget.origen, 'rechazados')}
                  >
                    Ver rechazados ({formatNumber(widget.rechazadas)})
                  </Button>
                ) : null}
              </div>
            </div>
          ))}

          <div className="flex flex-col rounded-card border border-brand-100 bg-brand-50/60 p-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-700">
              Total de documentos electrónicos
            </span>
            <p className="mt-1 text-2xl font-bold tabular-nums text-ink-900">
              {formatNumber(documentos.total)}
            </p>
            <ul className="mt-2 space-y-0.5">
              {documentos.porOrigen.map((origen) => (
                <li
                  key={origen.origen}
                  className="flex items-center justify-between gap-2 text-[11px] text-ink-500"
                >
                  <span className="truncate">{origen.etiqueta}</span>
                  <span className="font-mono tabular-nums text-ink-700">
                    {formatNumber(origen.total)}
                  </span>
                </li>
              ))}
            </ul>

            <Button
              variant="outline"
              size="sm"
              fullWidth
              className="mt-auto"
              onClick={() => ver('todos', 'todos')}
            >
              Ver todos los documentos
            </Button>
          </div>
        </div>

        {pendientes.total + rechazados.total === 0 ? (
          <Alert variant="success" title="Sin pendientes ni rechazados">
            Todos los comprobantes registrados tienen respuesta de SUNAT.
          </Alert>
        ) : (
          <Alert
            variant={rechazados.total > 0 ? 'error' : 'warning'}
            title={`${formatNumber(pendientes.total)} pendiente(s) y ${formatNumber(rechazados.total)} rechazado(s)`}
          >
            Facturas: {formatNumber(pendientes.factura)} / {formatNumber(rechazados.factura)} ·
            Boletas: {formatNumber(pendientes.boleta)} / {formatNumber(rechazados.boleta)} ·
            Administrativos: {formatNumber(pendientes.adminComprobantes)} /{' '}
            {formatNumber(rechazados.adminComprobantes)} · Market: {formatNumber(pendientes.market)} /{' '}
            {formatNumber(rechazados.market)}. (pendientes / rechazados)
          </Alert>
        )}

        <p className="text-xs text-ink-400">
          El total suma los comprobantes del POS (facturas y boletas), los administrativos y los
          del Market (sólo los que tienen número y empiezan por <span className="font-mono">F</span>{' '}
          o <span className="font-mono">B</span>).
        </p>
      </div>
    </SectionCard>
  );
}

/**
 * Servicios de la estación (`/configuracion/servicios`).
 *
 * Puerto de la pantalla *Servicios* del panel anterior, con lo que cambió en el
 * stack nuevo: ya no se arrancan ni se paran servicios del servidor (webtunnel,
 * ticketing y cola por `systemctl`). Las secciones van de lo más operativo a lo
 * más técnico: **estados de los comprobantes**, **boleteo automático** y **cola de
 * envío**, y al final la **conexión con el controlador PTS**.
 */
export function ServiciosPage() {
  useDocumentTitle('Configuración · Servicios de la estación');

  const pts = useServiciosPts();
  const estado = useEstadoServicios();

  const refrescar = () => {
    void pts.refetch();
    void estado.refetch();
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Server className="size-6 text-brand-500" aria-hidden="true" />
            Servicios de la estación
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-500">
            Comprobantes pendientes y rechazados, el boleteo automático, la cola de envío de
            documentos y la conexión con el controlador de surtidores. Todo lo que sostiene la
            operación, comprobado desde este panel.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="whitespace-nowrap"
          leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          loading={pts.isFetching || estado.isFetching}
          onClick={refrescar}
        >
          Actualizar
        </Button>
      </header>

      {estado.isLoading ? (
        <SectionCard title="Estados de los comprobantes">
          <Skeleton className="h-32 w-full" />
        </SectionCard>
      ) : null}

      {estado.data ? (
        <TarjetaComprobantes comprobantes={estado.data.cola.comprobantes} />
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          {estado.isLoading ? (
            <SectionCard title="Boleteo automático">
              <Skeleton className="h-32 w-full" />
            </SectionCard>
          ) : null}

          {estado.isError ? (
            <SectionCard title="Boleteo automático">
              <Alert variant="error" title="No se pudo leer el estado de los servicios">
                {mensajeError(estado.error, 'Vuelve a intentarlo en unos segundos.')}
              </Alert>
            </SectionCard>
          ) : null}

          {estado.data ? (
            <TarjetaBoleteo boleteo={estado.data.boleteo} sistema={estado.data.sistema} />
          ) : null}
        </div>

        <SectionCard
          title="Cola de envío"
          description="Documentos esperando su turno para emitirse o enviarse."
          action={
            <Send className="size-5 text-ink-300" aria-hidden="true" />
          }
        >
          {estado.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : estado.data ? (
            <div className="space-y-6">
              <BloqueOutbox outbox={estado.data.cola.outbox} />
              <BloqueComprobantes comprobantes={estado.data.cola.comprobantes} />

              <p className="flex items-center gap-1.5 text-xs text-ink-400">
                <HandCoins className="size-3.5" aria-hidden="true" />
                Revisado el {formatDateTimeValue(estado.data.revisadoEn, true)}.
              </p>
            </div>
          ) : (
            <p className="text-sm text-ink-500">Sin datos de la cola.</p>
          )}
        </SectionCard>
      </div>

      <TarjetaPts />

      <SectionCard
        title="Sobre estos servicios"
        description="Qué dejó de existir del panel anterior y qué lo reemplaza."
      >
        <ul className="space-y-2 text-sm text-ink-600">
          <li className="flex gap-2">
            <Cpu className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden="true" />
            <span>
              La conexión con el controlador se prueba con los paquetes de sólo lectura de jsonPTS:
              no se envía ninguna orden al equipo.
            </span>
          </li>
          <li className="flex gap-2">
            <Play className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden="true" />
            <span>
              El boleteo automático pertenece al modo <strong>WEB</strong> (
              <span className="font-mono text-xs">EDSUITE_USE_SYSTEM=WEB</span>); en modo APP los
              comprobantes los emite la app, así que la tarjeta queda deshabilitada. La cola de
              envío es del stack nuevo (no hay procesos de Windows ni banderas en disco) y aquí se
              ve su estado.
            </span>
          </li>
        </ul>
      </SectionCard>
    </div>
  );
}
